import type { Note } from '@tunasong/models'
import { skipToken, storageApi } from '@tunasong/redux'
import type { CoreElement, Persisted, SlateText } from '@tunasong/schemas'
import { textFromYDoc } from '@tunasong/sync-lib'

/** Text of the legacy `children` snapshot (stale relative to the YDoc). */
const childrenText = (children: CoreElement[] | null | undefined) =>
  Array.isArray(children)
    ? children
        .flatMap(block => (Array.isArray(block.children) ? block.children : []))
        .map(leaf => (typeof (leaf as SlateText).text === 'string' ? (leaf as SlateText).text : ''))
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim()
    : ''

const nonEmpty = (text: string | null | undefined) => (text && text.trim() ? text.trim() : null)

/**
 * A note's text. The live YDoc content lives in S3 (the inline `yDoc` field is only the seed), so it is
 * read from there when the signed URLs are valid; RTK Query shares that request across every reader.
 * Fallbacks: the inline seed (fresh or legacy notes), then the `children` snapshot.
 *
 * `likelyEdited`: no text arrived, but the note was edited after it was created, so its text is still
 * on its way (typical right after a create). Callers then show nothing rather than "empty".
 */
export const useNoteText = (note: Persisted<Note>) => {
  const validUrls =
    note.storageUrls?.expiresAtISODateTime !== undefined && note.storageUrls.expiresAtISODateTime > new Date().toISOString()
  const { currentData: yDocBytes } = storageApi.useGetAuthorizedStorageFileQuery(
    validUrls && note.storageUrls ? { storageUrls: note.storageUrls } : skipToken
  )
  const text =
    nonEmpty(textFromYDoc(yDocBytes)) ??
    nonEmpty(textFromYDoc(note.yDoc)) ??
    nonEmpty(childrenText(note.children as CoreElement[] | null | undefined))

  const created = note.createdAt ? new Date(note.createdAt).getTime() : 0
  const updated = note.updatedAt ? new Date(note.updatedAt).getTime() : 0
  const likelyEdited = Number.isFinite(created) && Number.isFinite(updated) && updated - created > 2_000

  return { text, likelyEdited }
}
